import { useMemo } from 'react';
import { useMutation, type Query } from '@tanstack/react-query';
import { AxiosError, isAxiosError } from 'axios';
import { graphql } from '~/graphql';
import {
  ExtractionStatus,
  type ExtractedDocumentItemsQuery,
  type ExtractedDocumentProgressQuery,
} from '~/graphql/graphql';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import { getApiClient } from '~/lib/axios';
import { multipartRequest } from '~/lib/multipart';
import { uploadErrorKey } from '~/utils/uploadError';

export const EXTRACTION_POLL_INTERVAL_MS = 1_500;
export const EXTRACTION_POLL_MAX_INTERVAL_MS = 12_000;
const POLL_BACKOFF_STEP_MS = 30_000;

export const TERMINAL_STATUSES: readonly ExtractionStatus[] = [
  ExtractionStatus.Complete,
  ExtractionStatus.NeedsReview,
  ExtractionStatus.Failed,
];

export type ExtractedDocumentProgress = NonNullable<
  ExtractedDocumentProgressQuery['extractedDocument']
>;

export type UnitProgress = ExtractedDocumentProgress['units'][number];

export type ExtractedItem = NonNullable<
  ExtractedDocumentItemsQuery['extractedDocument']
>['items'][number];

export type ExtractedField = ExtractedItem['fields'][number];

export type ExtractedDocumentView = ExtractedDocumentProgress & {
  items: ExtractedItem[];
};

/** A refused upload; a screen shows `messageKey`, interpolating `maxSizeMB`. */
export class UploadRejection extends Error {
  readonly messageKey: string;

  constructor(messageKey: string) {
    super(messageKey);
    this.name = 'UploadRejection';
    this.messageKey = messageKey;
  }
}

const UPLOAD_FAILED_KEY = 'document-extraction:upload.unknown';
const UPLOAD_TIMED_OUT_KEY = 'document-extraction:upload.timed_out';

const STATUSES_WITH_ITEMS: readonly ExtractionStatus[] = [
  ExtractionStatus.Complete,
  ExtractionStatus.NeedsReview,
];

/** A failed document stops polling but has no rows to fetch. */
export function hasItems(status: ExtractionStatus | undefined): boolean {
  return status !== undefined && STATUSES_WITH_ITEMS.includes(status);
}

export function isTerminal(status: ExtractionStatus | undefined): boolean {
  return status !== undefined && TERMINAL_STATUSES.includes(status);
}

/**
 * Each poll re-reads the document's whole units column, so the rate backs off as the run ages:
 * a document that stalls costs a few reads a minute rather than forty.
 */
export function refetchIntervalFor(
  status: ExtractionStatus | undefined,
  ageMs = 0
): number | false {
  if (status === undefined || isTerminal(status)) {
    return false;
  }
  const steps = Math.floor(Math.max(ageMs, 0) / POLL_BACKOFF_STEP_MS);
  return Math.min(
    EXTRACTION_POLL_INTERVAL_MS * 2 ** steps,
    EXTRACTION_POLL_MAX_INTERVAL_MS
  );
}

/**
 * A failed refetch keeps polling at the slowest rate rather than stopping: nothing else restarts
 * the interval, so one 502 during a deploy would otherwise freeze a running document on screen.
 */
export function pollIntervalOf(
  query: Query<ExtractedDocumentProgressQuery>
): number | false {
  const document = query.state.data?.extractedDocument;
  if (query.state.status === 'error') {
    if (document == null) {
      return false;
    }
    return isTerminal(document.status)
      ? false
      : EXTRACTION_POLL_MAX_INTERVAL_MS;
  }
  // By run age, not by how long this screen has watched: a run that is already long is the one
  // likely to keep running.
  return refetchIntervalFor(document?.status, ageOf(document?.createdAt));
}

function ageOf(createdAt: string | null | undefined): number {
  if (!createdAt) {
    return 0;
  }
  const started = Date.parse(createdAt);
  return Number.isNaN(started) ? 0 : Date.now() - started;
}

const extractedDocumentProgressQuery = graphql(`
  query ExtractedDocumentProgress($id: ID!) {
    extractedDocument(id: $id) {
      id
      createdAt
      originalFilename
      sourceFormat
      documentKind
      status
      failureReason
      vendorName
      documentDate
      currency
      priceBasis
      notes
      ambiguities
      expectedPositionCount
      coveredPositionCount
      units {
        documentId
        sourceFilename
        unitKey
        label
        representation
        status
        expectedPositionCount
        coveredPositionCount
        attemptCount
      }
    }
  }
`);

const extractedDocumentItemsQuery = graphql(`
  query ExtractedDocumentItems($id: ID!) {
    extractedDocument(id: $id) {
      id
      items {
        id
        documentId
        unitLabel
        positionKey
        provenance
        verdict
        verifiedValue
        governingLabels
        claimedValue
        secondOpinion
        secondOpinionReason
        confidence
        ambiguities
        fields {
          name
          value
        }
      }
    }
  }
`);

export function useExtractedDocument(documentId: string | undefined) {
  const variables = { id: documentId ?? '' };
  const progress = useGraphqlQuery(
    extractedDocumentProgressQuery,
    ['extractedDocument', documentId, 'progress'],
    variables,
    { enabled: Boolean(documentId), refetchInterval: pollIntervalOf }
  );
  const summary = progress.data?.extractedDocument ?? undefined;

  const items = useGraphqlQuery(
    extractedDocumentItemsQuery,
    ['extractedDocument', documentId, 'items'],
    variables,
    { enabled: Boolean(documentId) && hasItems(summary?.status) }
  );
  const extractedItems = items.data?.extractedDocument?.items;

  const document = useMemo<ExtractedDocumentView | undefined>(
    () => summary && { ...summary, items: extractedItems ?? [] },
    [summary, extractedItems]
  );
  const itemsExpected = hasItems(summary?.status);
  return {
    document,
    isLoading: Boolean(documentId) && progress.isPending,
    isLoadingItems: itemsExpected && items.isPending,
    // A failed refetch keeps the last answer; only a query with nothing to show counts as failed.
    isError:
      (progress.isError && summary === undefined) ||
      (itemsExpected && items.isError && extractedItems === undefined),
    isMissing: progress.isSuccess && progress.data?.extractedDocument == null,
    refetch: () => {
      void progress.refetch();
    },
  };
}

export function useUploadDocument() {
  const apiClient = getApiClient();

  return useMutation<{ id: string }, UploadRejection, File>({
    mutationFn: async (file) => {
      const payload = new FormData();
      payload.append('file', file);

      let data: unknown;
      try {
        ({ data } = await apiClient({
          method: 'post',
          url: '/api/documents/extract',
          ...multipartRequest(payload),
        }));
      } catch (error) {
        throw rejectionOf(error);
      }
      if (typeof (data as { id?: unknown } | undefined)?.id !== 'string') {
        console.error('Unexpected upload response shape', data);
        throw new UploadRejection(UPLOAD_FAILED_KEY);
      }
      return data as { id: string };
    },
  });
}

function rejectionOf(error: unknown): UploadRejection {
  if (!isAxiosError(error)) {
    console.error('Upload failed before reaching the server', error);
    return new UploadRejection(UPLOAD_FAILED_KEY);
  }
  if (error.code === AxiosError.ECONNABORTED) {
    return new UploadRejection(UPLOAD_TIMED_OUT_KEY);
  }
  return new UploadRejection(
    uploadErrorKey(error.response?.data) ?? UPLOAD_FAILED_KEY
  );
}
